<template>
	<div class="content">
		<header class="page-header">
			<span class="page-header-emoji">🔐</span>
			<div class="page-header-text">
				<div class="page-header-title">账号密码</div>
				<div class="page-header-sub">共 {{ list.length }} 条</div>
			</div>
		</header>

		<section class="filter-section">
			<van-search
				v-model="searchKeyword"
				shape="round"
				background="transparent"
				placeholder="搜索站点、账号、网址、备注"
				show-action
				@search="loadList"
				@clear="clearSearch"
			>
				<template #action>
					<div class="search-action" @click="loadList">搜索</div>
				</template>
			</van-search>
		</section>

		<div class="panel-scroll">
			<div v-if="loading && list.length === 0" class="empty-tip">加载中...</div>
			<div v-else-if="list.length === 0" class="empty-tip">
				<span class="empty-emoji">🔒</span>
				<span>暂无账号记录</span>
			</div>

			<div v-else class="card-list">
				<article
					class="vault-card"
					:class="{ 'vault-card--expanded': isExpanded(item.id) }"
					v-for="item in list"
					:key="item.id"
				>
					<button type="button" class="vault-card-head" @click="toggleCard(item.id)">
						<div class="vault-card-head-main">
							<span class="site-name">{{ item.site_name }}</span>
							<span v-if="item.account" class="account-preview">{{ item.account }}</span>
						</div>
						<span class="expand-icon">{{ isExpanded(item.id) ? '▾' : '▸' }}</span>
					</button>
					<div v-show="isExpanded(item.id)" class="vault-card-body">
						<div v-if="item.url" class="field-row">
							<span class="field-label">网址</span>
							<button type="button" class="field-value field-value--link" @click="openUrl(item.url)">
								{{ item.url }}
							</button>
						</div>
						<div v-if="item.account" class="field-row">
							<span class="field-label">账号</span>
							<button type="button" class="field-value" @click="copyText(item.account, '账号')">
								{{ item.account }}
							</button>
						</div>
						<div v-if="item.password" class="field-row">
							<span class="field-label">密码</span>
							<div class="password-row">
								<button type="button" class="field-value field-value--password" @click="copyText(item.password, '密码')">
									{{ isPasswordVisible(item.id) ? item.password : '••••••••' }}
								</button>
								<button type="button" class="toggle-btn" @click="togglePassword(item.id)">
									{{ isPasswordVisible(item.id) ? '隐藏' : '显示' }}
								</button>
							</div>
						</div>
						<div v-if="item.remark" class="field-row">
							<span class="field-label">备注</span>
							<span class="field-text">{{ item.remark }}</span>
						</div>
						<div class="vault-card-foot">
							<span class="time-text">{{ formatTime(item.update_time || item.create_time) }}</span>
							<div class="action-row">
								<button type="button" class="action-chip" @click="openEditDialog(item)">编辑</button>
								<button
									v-if="item.password"
									type="button"
									class="action-chip"
									@click="copyText(item.password, '密码')"
								>
									复制密码
								</button>
								<button type="button" class="action-chip action-chip--danger" @click="confirmDelete(item)">
									删除
								</button>
							</div>
						</div>
					</div>
				</article>
			</div>
		</div>

		<button type="button" class="add-fab" aria-label="添加账号" @click="openAddDialog">+</button>

		<van-dialog
			v-model="showFormDialog"
			:title="formId ? '编辑账号' : '添加账号'"
			show-cancel-button
			:before-close="beforeFormClose"
		>
			<div class="form-dialog-body">
				<van-field v-model="form.siteName" label="站点" placeholder="如：微信、GitHub" required clearable />
				<van-field v-model="form.account" label="账号" placeholder="用户名/手机号/邮箱" clearable />
				<van-field
					v-model="form.password"
					label="密码"
					:type="showFormPassword ? 'text' : 'password'"
					placeholder="登录密码"
					clearable
				>
					<template #button>
						<span class="form-eye" @click="showFormPassword = !showFormPassword">
							{{ showFormPassword ? '隐藏' : '显示' }}
						</span>
					</template>
				</van-field>
				<van-field v-model="form.url" label="网址" placeholder="https://..." clearable />
				<van-field v-model="form.remark" label="备注" placeholder="可选" clearable />
			</div>
		</van-dialog>

		<van-dialog
			v-model="showDeleteDialog"
			title="删除账号"
			show-cancel-button
			@confirm="deleteItem"
		>
			<div class="delete-dialog-body">
				<div>确定删除这条账号记录吗？</div>
				<div class="delete-dialog-name">{{ deleteTarget?.site_name }}</div>
			</div>
		</van-dialog>
	</div>
</template>

<script>
import { Notify } from 'vant'
import {
	getAccountVaultList,
	addAccountVault,
	updateAccountVault,
	deleteAccountVault,
} from '../util/api'

export default {
	name: 'AccountVault',
	data() {
		return {
			list: [],
			loading: false,
			searchKeyword: '',
			expandedIds: [],
			visiblePasswordIds: [],
			showFormDialog: false,
			showFormPassword: false,
			formId: null,
			form: {
				siteName: '',
				account: '',
				password: '',
				url: '',
				remark: '',
			},
			saving: false,
			showDeleteDialog: false,
			deleteTarget: null,
			deleting: false,
		}
	},
	methods: {
		loadList() {
			this.loading = true
			const params = {}
			if (this.searchKeyword && this.searchKeyword.trim()) {
				params.keyword = this.searchKeyword.trim()
			}
			getAccountVaultList(params)
				.then((res) => {
					this.list = res.data?.data || []
					this.expandedIds = this.expandedIds.filter((id) =>
						this.list.some((item) => item.id === id)
					)
					this.visiblePasswordIds = this.visiblePasswordIds.filter((id) =>
						this.list.some((item) => item.id === id)
					)
				})
				.finally(() => {
					this.loading = false
				})
		},
		clearSearch() {
			this.searchKeyword = ''
			this.loadList()
		},
		isExpanded(id) {
			return this.expandedIds.includes(id)
		},
		toggleCard(id) {
			if (this.isExpanded(id)) {
				this.expandedIds = this.expandedIds.filter((itemId) => itemId !== id)
				return
			}
			this.expandedIds = [...this.expandedIds, id]
		},
		isPasswordVisible(id) {
			return this.visiblePasswordIds.includes(id)
		},
		togglePassword(id) {
			if (this.isPasswordVisible(id)) {
				this.visiblePasswordIds = this.visiblePasswordIds.filter((itemId) => itemId !== id)
				return
			}
			this.visiblePasswordIds = [...this.visiblePasswordIds, id]
		},
		resetForm() {
			this.formId = null
			this.form = {
				siteName: '',
				account: '',
				password: '',
				url: '',
				remark: '',
			}
			this.showFormPassword = false
		},
		openAddDialog() {
			this.resetForm()
			this.showFormDialog = true
		},
		openEditDialog(item) {
			this.formId = item.id
			this.form = {
				siteName: item.site_name || '',
				account: item.account || '',
				password: item.password || '',
				url: item.url || '',
				remark: item.remark || '',
			}
			this.showFormPassword = false
			this.showFormDialog = true
		},
		beforeFormClose(action, done) {
			if (action !== 'confirm') {
				this.resetForm()
				done()
				return
			}
			if (!this.form.siteName || !this.form.siteName.trim()) {
				Notify({ type: 'danger', message: '请填写站点名称' })
				done(false)
				return
			}
			if (this.saving) {
				done(false)
				return
			}
			this.saving = true
			const payload = {
				siteName: this.form.siteName.trim(),
				account: this.form.account,
				password: this.form.password,
				url: this.form.url,
				remark: this.form.remark,
			}
			const request = this.formId
				? updateAccountVault({ id: this.formId, ...payload })
				: addAccountVault(payload)
			request
				.then((res) => {
					if (res.data?.code === 0) {
						Notify({ type: 'success', message: this.formId ? '更新成功' : '添加成功' })
						this.loadList()
						this.resetForm()
						done()
					} else {
						Notify({ type: 'danger', message: res.data?.message || '保存失败' })
						done(false)
					}
				})
				.catch(() => {
					Notify({ type: 'danger', message: '保存失败' })
					done(false)
				})
				.finally(() => {
					this.saving = false
				})
		},
		confirmDelete(item) {
			this.deleteTarget = item
			this.showDeleteDialog = true
		},
		deleteItem() {
			if (!this.deleteTarget?.id || this.deleting) return
			this.deleting = true
			deleteAccountVault({ id: this.deleteTarget.id })
				.then((res) => {
					if (res.data?.code === 0) {
						Notify({ type: 'success', message: '删除成功' })
						this.loadList()
					} else {
						Notify({ type: 'danger', message: res.data?.message || '删除失败' })
					}
				})
				.finally(() => {
					this.deleting = false
					this.deleteTarget = null
				})
		},
		openUrl(url) {
			if (!url) return
			const target = /^https?:\/\//i.test(url) ? url : `https://${url}`
			window.open(target, '_blank')
		},
		copyText(text, label) {
			if (!text) return
			if (navigator.clipboard && navigator.clipboard.writeText) {
				navigator.clipboard.writeText(text).then(() => {
					Notify({ type: 'success', message: `已复制${label}` })
				}).catch(() => {
					this.fallbackCopy(text, label)
				})
			} else {
				this.fallbackCopy(text, label)
			}
		},
		fallbackCopy(text, label) {
			const input = document.createElement('textarea')
			input.value = text
			document.body.appendChild(input)
			input.select()
			try {
				document.execCommand('copy')
				Notify({ type: 'success', message: `已复制${label}` })
			} catch (e) {
				Notify({ type: 'danger', message: '复制失败' })
			}
			document.body.removeChild(input)
		},
		formatTime(time) {
			if (!time) return ''
			const date = new Date(time)
			if (Number.isNaN(date.getTime())) return time
			const y = date.getFullYear()
			const m = date.getMonth() + 1
			const d = date.getDate()
			const h = String(date.getHours()).padStart(2, '0')
			const min = String(date.getMinutes()).padStart(2, '0')
			return `${y}/${m}/${d} ${h}:${min}`
		},
	},
	mounted() {
		this.loadList()
	},
}
</script>

<style scoped>
.content {
	--q-bg0: #fff7fb;
	--q-bg1: #f2fbff;
	--q-card: #ffffff;
	--q-line: rgba(15, 23, 42, 0.07);
	--q-shadow-sm: 0 4px 16px rgba(17, 24, 39, 0.06);
	--q-radius: 16px;
	--q-accent: #7c3aed;
	--q-accent-soft: rgba(124, 58, 237, 0.1);

	padding: 12px 12px 72px;
	display: flex;
	flex-direction: column;
	height: 100%;
	min-height: 0;
	overflow: hidden;
	box-sizing: border-box;
	font-family: "Microsoft YaHei UI", "PingFang SC", "Hiragino Sans GB", system-ui, -apple-system, sans-serif;
	background: radial-gradient(120% 90% at 20% 0%, var(--q-bg0) 0%, transparent 60%),
		radial-gradient(120% 90% at 90% 30%, var(--q-bg1) 0%, transparent 55%),
		#f8fafc;
}

.page-header {
	display: flex;
	align-items: center;
	gap: 12px;
	flex-shrink: 0;
	margin-bottom: 8px;
	padding: 14px 16px;
	border: 1px solid var(--q-line);
	border-radius: var(--q-radius);
	background: var(--q-card);
	box-shadow: var(--q-shadow-sm);
}

.page-header-emoji {
	font-size: 26px;
	line-height: 1;
}

.page-header-title {
	font-size: 17px;
	font-weight: 700;
	color: #111827;
}

.page-header-sub {
	font-size: 12px;
	color: #94a3b8;
	margin-top: 3px;
}

.filter-section {
	flex-shrink: 0;
	margin-bottom: 10px;
	padding: 0 2px;
}

.filter-section >>> .van-search {
	padding: 0;
}

.filter-section >>> .van-search__content {
	background: var(--q-card);
	border: 1px solid var(--q-line);
	box-shadow: var(--q-shadow-sm);
}

.search-action {
	font-size: 14px;
	font-weight: 600;
	color: var(--q-accent);
	padding: 0 4px;
}

.panel-scroll {
	flex: 1;
	min-height: 0;
	overflow-y: auto;
	-webkit-overflow-scrolling: touch;
}

.empty-tip {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 8px;
	color: #94a3b8;
	font-size: 14px;
	min-height: 140px;
}

.empty-emoji {
	font-size: 32px;
	line-height: 1;
}

.card-list {
	display: flex;
	flex-direction: column;
	gap: 10px;
}

.vault-card {
	padding: 10px 12px;
	border: 1px solid var(--q-line);
	border-radius: var(--q-radius);
	background: var(--q-card);
	box-shadow: var(--q-shadow-sm);
}

.vault-card--expanded {
	padding-bottom: 14px;
}

.vault-card-head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 10px;
	width: 100%;
	margin: 0;
	padding: 4px 2px;
	border: none;
	background: none;
	text-align: left;
	cursor: pointer;
}

.vault-card-head-main {
	display: flex;
	flex-direction: column;
	gap: 4px;
	min-width: 0;
	flex: 1;
}

.site-name {
	font-size: 15px;
	font-weight: 700;
	color: #111827;
}

.account-preview {
	font-size: 12px;
	color: #64748b;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.expand-icon {
	flex-shrink: 0;
	font-size: 14px;
	color: #94a3b8;
}

.vault-card-body {
	margin-top: 10px;
	padding-top: 10px;
	border-top: 1px dashed var(--q-line);
}

.field-row {
	display: flex;
	flex-direction: column;
	gap: 4px;
	margin-bottom: 10px;
}

.field-label {
	font-size: 11px;
	font-weight: 600;
	color: #94a3b8;
}

.field-value {
	display: block;
	width: 100%;
	padding: 8px 10px;
	border: 1px solid rgba(15, 23, 42, 0.06);
	border-radius: 10px;
	background: #f8fafc;
	font-size: 13px;
	line-height: 1.5;
	color: #334155;
	word-break: break-all;
	text-align: left;
	cursor: pointer;
}

.field-value--link {
	color: var(--q-accent);
}

.field-value--password {
	flex: 1;
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
}

.field-text {
	font-size: 13px;
	color: #475569;
	line-height: 1.5;
	word-break: break-all;
}

.password-row {
	display: flex;
	align-items: center;
	gap: 8px;
}

.toggle-btn {
	flex-shrink: 0;
	border: 1px solid rgba(124, 58, 237, 0.2);
	background: var(--q-accent-soft);
	color: var(--q-accent);
	padding: 6px 10px;
	border-radius: 999px;
	font-size: 12px;
	font-weight: 600;
}

.vault-card-foot {
	display: flex;
	flex-direction: column;
	gap: 10px;
	margin-top: 4px;
	padding-top: 10px;
	border-top: 1px dashed var(--q-line);
}

.time-text {
	font-size: 11px;
	color: #94a3b8;
}

.action-row {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
}

.action-chip {
	border: 1px solid rgba(124, 58, 237, 0.2);
	background: var(--q-accent-soft);
	color: var(--q-accent);
	padding: 5px 10px;
	border-radius: 999px;
	font-size: 12px;
	font-weight: 600;
	line-height: 1;
}

.action-chip--danger {
	border-color: rgba(239, 68, 68, 0.2);
	background: rgba(239, 68, 68, 0.08);
	color: #dc2626;
}

.add-fab {
	position: fixed;
	right: 20px;
	bottom: 88px;
	width: 52px;
	height: 52px;
	border: none;
	border-radius: 50%;
	background: linear-gradient(135deg, #7c3aed, #a78bfa);
	color: #fff;
	font-size: 28px;
	font-weight: 300;
	line-height: 1;
	box-shadow: 0 8px 24px rgba(124, 58, 237, 0.35);
	z-index: 10;
}

.form-dialog-body {
	padding: 8px 0 12px;
}

.form-eye {
	font-size: 12px;
	color: var(--q-accent);
	font-weight: 600;
}

.delete-dialog-body {
	padding: 16px 20px;
	font-size: 14px;
	color: #374151;
	line-height: 1.6;
}

.delete-dialog-name {
	margin-top: 8px;
	font-size: 15px;
	font-weight: 700;
	color: #111827;
}
</style>
